import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { KeyboardSafeForm } from '../../components/KeyboardSafeForm';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  RequestFormDateField,
  RequestFormTextField,
  RequestFormTimeField,
} from '../../components/RequestFormField';
import { RequestFormSelectField } from '../../components/RequestFormSelectField';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnderlineTabs } from '../../components/UnderlineTabs';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getTimePermissionModuleCapabilities } from '../../config/timePermissionCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  applyTimePermissionRequest,
  getTimePermissionApplyCatalog,
  getTimePermissionRequests,
} from '../../services/timePermissionService';
import type {
  TimePermissionOrgConfiguration,
  TimePermissionRequestListItem,
  TimePermissionType,
} from '../../types/timePermission';
import {
  formatCalendarDate,
  formatMinutes,
  formatPermissionTime,
} from '../../utils/dateTime';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { timePermissionErrorMessage } from '../../utils/timePermissionErrors';
import { getTimePermissionStatusCategory } from '../../utils/timePermissionStatus';
import {
  canSeeRequestApprovals,
  permissionApprovalListTitle,
} from '../../utils/approvalRoles';
import { parseClockToMinutes } from '../../utils/checkoutBreak';
import { isValidLeaveDate } from '../../utils/leaveFilters';
import {
  isPermissionOrgConfigured,
  validateTimePermissionApply,
} from '../../utils/timePermissionApply';

type Props = NativeStackScreenProps<RootStackParamList, 'TimePermissionHome'>;
type PermissionTab = 'My Permissions' | 'Apply Permission';

const PERMISSION_TABS: readonly PermissionTab[] = [
  'My Permissions',
  'Apply Permission',
];

function statusTextColor(statusCode: string | null, palette: ColorPalette) {
  const category = getTimePermissionStatusCategory(statusCode);
  if (category === 'success') return palette.success;
  if (category === 'danger') return palette.error;
  if (category === 'warning') return palette.warning;
  if (category === 'info') return palette.info;
  return palette.textSecondary;
}

function RequestRow({
  item,
  onPress,
  palette,
}: {
  item: TimePermissionRequestListItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const dateLabel = formatCalendarDate(item.permissionDate);
  const timeRange = `${formatPermissionTime(item.fromTime)} - ${formatPermissionTime(item.toTime)}`;
  const reason =
    item.reasonSummary ||
    formatDisplayLabel(
      item.permissionTypeName || item.permissionTypeCode,
      'Permission',
    );

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${dateLabel}, ${item.statusLabel}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowLeft}>
        <Text style={[styles.rowTitle, { color: palette.textPrimary }]}>
          {dateLabel}
        </Text>
        <Text style={[styles.rowMeta, { color: palette.textSecondary }]}>
          {timeRange}
        </Text>
      </View>
      <View style={styles.rowRight}>
        <Text
          style={[
            styles.statusText,
            { color: statusTextColor(item.statusCode, palette) },
          ]}
        >
          {item.statusLabel}
        </Text>
        <Text style={[styles.rowMeta, { color: palette.textSecondary }]}>
          {reason}
        </Text>
      </View>
    </Pressable>
  );
}

function ApplyPermissionForm({
  canApply,
  palette,
  onSuccess,
}: {
  canApply: boolean;
  palette: ColorPalette;
  onSuccess: () => void;
}) {
  const [permissionDate, setPermissionDate] = useState('');
  const [permissionTypes, setPermissionTypes] = useState<TimePermissionType[]>(
    [],
  );
  const [configuration, setConfiguration] =
    useState<TimePermissionOrgConfiguration | null>(null);
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const selectedType =
    permissionTypes.find(type => type.permissionTypeCode === selectedTypeCode) ??
    null;
  const permissionTypeOptions = permissionTypes.flatMap(type => {
    const value = type.permissionTypeCode?.trim();
    if (!value) return [];
    return [
      {
        value,
        label: type.permissionTypeName?.trim() || value,
      },
    ];
  });

  const loadTypes = useCallback(
    async (dateValue = permissionDate) => {
      setTypesLoading(true);
      try {
        const catalog = await getTimePermissionApplyCatalog(
          isValidLeaveDate(dateValue) ? dateValue : undefined,
        );
        setPermissionTypes(catalog.types);
        setConfiguration(catalog.configuration);
        setSelectedTypeCode(current => {
          if (
            current &&
            catalog.types.some(type => type.permissionTypeCode === current)
          ) {
            return current;
          }
          return catalog.types[0]?.permissionTypeCode ?? '';
        });
        setTypesError(null);
      } catch (error) {
        setPermissionTypes([]);
        setConfiguration(null);
        setSelectedTypeCode('');
        setTypesError(
          timePermissionErrorMessage(
            error,
            'Unable to load permission types.',
          ),
        );
      } finally {
        setTypesLoading(false);
      }
    },
    [permissionDate],
  );

  useEffect(() => {
    loadTypes().catch(() => undefined);
  }, [loadTypes]);

  const submit = async () => {
    if (!canApply || submitting) return;
    const trimmedDate = permissionDate.trim();
    const trimmedType = selectedType?.permissionTypeCode?.trim().toUpperCase();
    const trimmedFrom = fromTime.trim();
    const trimmedTo = toTime.trim();
    const validation = validateTimePermissionApply({
      permissionDateValid: isValidLeaveDate(trimmedDate),
      permissionTypeCode: trimmedType,
      fromMinutes: parseClockToMinutes(trimmedFrom),
      toMinutes: parseClockToMinutes(trimmedTo),
      reason,
      configuration,
    });
    if (validation) {
      setSubmitError(validation);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await applyTimePermissionRequest({
        permissionDate: trimmedDate,
        permissionType: trimmedType as string,
        startTime: trimmedFrom,
        endTime: trimmedTo,
        reason: reason.trim(),
      });
      setPermissionDate('');
      setSelectedTypeCode(permissionTypes[0]?.permissionTypeCode ?? '');
      setFromTime('');
      setToTime('');
      setReason('');
      onSuccess();
    } catch (error) {
      setSubmitError(
        timePermissionErrorMessage(
          error,
          'Unable to submit permission request.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <KeyboardSafeForm>
      <RequestFormDateField
        label="Date"
        required
        value={permissionDate}
        onChange={value => {
          setPermissionDate(value);
          setSubmitError(null);
        }}
        editable={canApply}
      />
      <RequestFormSelectField
        label="Permission Type"
        required
        value={selectedTypeCode}
        options={permissionTypeOptions}
        onChange={value => {
          setSelectedTypeCode(value);
          setSubmitError(null);
        }}
        placeholder={
          typesLoading ? 'Loading permission types…' : 'Select permission type'
        }
        editable={canApply && !typesLoading && permissionTypeOptions.length > 0}
      />
      {isPermissionOrgConfigured(configuration) ? (
        <Text style={[styles.rowMeta, { color: palette.textSecondary }]}>
          {configuration.remainingMonth} of {configuration.maximumPermissionsPerMonth}{' '}
          permissions remaining this month. Duration must be{' '}
          {formatMinutes(configuration.minimumDurationMinutes)}–
          {formatMinutes(configuration.maximumDurationMinutes)}.
        </Text>
      ) : null}
      <RequestFormTimeField
        label="From Time"
        required
        value={fromTime}
        onChange={value => {
          setFromTime(value);
          setSubmitError(null);
        }}
        placeholder="Select time"
        editable={canApply}
      />
      <RequestFormTimeField
        label="To Time"
        required
        value={toTime}
        onChange={value => {
          setToTime(value);
          setSubmitError(null);
        }}
        placeholder="Select time"
        editable={canApply}
      />
      <RequestFormTextField
        label="Reason"
        required
        value={reason}
        onChangeText={value => {
          setReason(value);
          setSubmitError(null);
        }}
        placeholder="Enter reason"
        multiline
        editable={canApply}
      />
      {typesError ? (
        <Text accessibilityRole="alert" style={[styles.warning, { color: palette.error }]}>
          {typesError}
        </Text>
      ) : null}
      {!typesLoading && !typesError && permissionTypes.length === 0 && canApply ? (
        <Text style={[styles.warning, { color: palette.warning }]}>
          No permission types are configured. Ask HR to add a permission type.
        </Text>
      ) : null}
      {submitError ? (
        <Text accessibilityRole="alert" style={[styles.warning, { color: palette.error }]}>
          {submitError}
        </Text>
      ) : null}
      {canApply ? (
        <AppButton
          title="Submit Request"
          onPress={submit}
          loading={submitting}
          disabled={submitting || typesLoading || permissionTypes.length === 0}
        />
      ) : (
        <AppButton
          title="Submit Request"
          disabled
          onPress={() => undefined}
        />
      )}
    </KeyboardSafeForm>
  );
}

export function TimePermissionHomeScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getTimePermissionModuleCapabilities(auth.permissions);
  const showTeamApprovals = canSeeRequestApprovals(
    auth.roles,
    auth.permissions,
    PERMISSIONS.PERMISSION_APPROVE,
  );

  const [activeTab, setActiveTab] = useState<PermissionTab>('My Permissions');
  const [items, setItems] = useState<TimePermissionRequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const openDetails = useCallback(
    (item: TimePermissionRequestListItem) => {
      navigation.navigate(routes.timePermissionDetails, {
        permissionRequestId: item.permissionRequestId,
      });
    },
    [navigation],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canViewHistory) {
        setItems([]);
        setHistoryError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const result = await getTimePermissionRequests({
          page: 1,
          pageSize: 20,
        });
        setItems(result.items);
        setHistoryError(null);
      } catch (error) {
        setItems([]);
        setHistoryError(
          timePermissionErrorMessage(
            error,
            'Unable to load permission requests.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canViewHistory],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const header = useMemo(
    () => (
      <ScreenHeader
        bleed={false}
        title={
        activeTab === 'Apply Permission'
        ? 'Apply Permission'
        : 'Permission Requests'
        }
        onBack={navigation.goBack}
      />
    ),
    [activeTab],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.TIME_PERMISSION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && items.length === 0 && !historyError ? (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          <View style={styles.center}>
            <AppLoader message="Loading permission requests" />
          </View>
        </AppScreen>
      ) : (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          {capabilities.canApply ? (
            <UnderlineTabs
              options={PERMISSION_TABS}
              value={activeTab}
              onChange={setActiveTab}
            />
          ) : null}
          {showTeamApprovals ? (
            <View style={styles.approvalAction}>
              <AppButton
                title={permissionApprovalListTitle(auth.roles)}
                onPress={() =>
                  navigation.navigate(routes.adminTimePermissionRequests)
                }
              />
            </View>
          ) : null}

          {activeTab === 'My Permissions' || !capabilities.canApply ? (
            <View style={styles.body}>
              {historyError && items.length === 0 ? (
                <ErrorState message={historyError} onRetry={() => load()} />
              ) : (
                <FlatList
                  data={items}
                  keyExtractor={item => String(item.permissionRequestId)}
                  refreshControl={
                    <RefreshControl
                      refreshing={refreshing}
                      onRefresh={() => load(true)}
                      tintColor={palette.primary}
                    />
                  }
                  ListEmptyComponent={
                    <EmptyState
                      title="No permission requests"
                      message="No permission requests were found."
                      actionTitle="Retry"
                      onAction={() => load()}
                    />
                  }
                  contentContainerStyle={styles.listContent}
                  renderItem={({ item }) => (
                    <RequestRow
                      item={item}
                      onPress={() => openDetails(item)}
                      palette={palette}
                    />
                  )}
                />
              )}
              {capabilities.canApply ? (
                <View style={styles.footer}>
                  <AppButton
                    title="Apply Permission"
                    onPress={() => setActiveTab('Apply Permission')}
                  />
                </View>
              ) : null}
            </View>
          ) : (
            <ApplyPermissionForm
              canApply={capabilities.canApply}
              palette={palette}
              onSuccess={() => {
                setActiveTab('My Permissions');
                load().catch(() => undefined);
              }}
            />
          )}
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  center: { flex: 1, justifyContent: 'center' },
  body: { flex: 1 },
  approvalAction: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.md },
  row: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  rowLeft: { flex: 1, gap: spacing.xs },
  rowRight: { alignItems: 'flex-end', gap: spacing.xs, maxWidth: '48%' },
  rowTitle: { ...typography.body, fontWeight: '700' },
  rowMeta: { ...typography.caption },
  statusText: { ...typography.body, fontWeight: '700' },
  footer: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  applyScreen: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
  },
  form: { gap: spacing.lg },
  warning: { ...typography.caption },
});
